const { default: getFrontConfig } = require('../getFrontConfig');

const FRONT_CONFIG = getFrontConfig();
const type = require('../utils/type');

/**
 * @typedef Price
 * @type Object
 * @property {number} value - Price numeric value
 * @property {string} formatted - Price formatted value
 * @property {string} currency - Site currency
 * @property {function} add - Function to add a number|price
 * @property {function} multiply - Function to multiply a number|price
 * @property {function} subtract - Function to subtract a number|price
 */

const proto = {
  value: null,
  formatted: '',
  currency: FRONT_CONFIG.defaultCurrency,
  add(otherPrice) {
    const sum = type.isNumber(otherPrice) ? otherPrice : otherPrice.value;
    return createPrice(this.value + sum);
  },
  multiply(otherPrice) {
    const multiply = type.isNumber(otherPrice) ? otherPrice : otherPrice.value;
    return createPrice(this.value * multiply);
  },
  substract(otherPrice) {
    const substract = type.isNumber(otherPrice) ? otherPrice : otherPrice.value;
    return createPrice(this.value - substract);
  },
};

/**
 * Creates a Price object from a number
 * @param  {number|string} priceNumber
 * @param {Object} options number format options for Intl.NumberFormat() constructor
 * @returns {Price} Price object with value, formatted value and currency symbol
 */
function createPrice(priceNumber, options = {}) {
  let priceValue = null;
  let formattedPrice = '';
  if (type.isString(priceNumber)) {
    priceValue = getNumberFromFormattedPrice(priceNumber);
    formattedPrice = priceNumber;
  } else if (type.isNumber(priceNumber)) {
    priceValue = priceNumber;
    formattedPrice = formatPrice(priceNumber, options);
  }

  const instance = Object.create(proto);
  instance.value = priceValue;
  instance.formatted = formattedPrice;

  return instance;
}

/**
 * Formats price according to the currentSite NumberFormat
 * @param  {number} priceNumber
 * @param {Object} options number format options for Intl.NumberFormat() constructor
 * @returns {string} formatted price
 */
function formatPrice(priceNumber, options = {}) {
  const formattedLocale = FRONT_CONFIG.siteLocale.replace('_', '-');
  const priceHasDecimals = priceNumber % 1 !== 0;
  const numberOfFractionDigits = priceHasDecimals ? 2 : 0;

  const defaultOptions = {
    style: 'currency',
    currency: FRONT_CONFIG.defaultCurrency,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: numberOfFractionDigits,
    ...options,
  };
  try {
    return new Intl.NumberFormat(formattedLocale, defaultOptions).format(
      priceNumber
    );
  } catch {
    return priceNumber.toString();
  }
}

/**
 * Returns the number from a formatted price given
 * @param  {string} formattedPrice - An string with the price and the currency symbol
 * @returns {number} The price as a number, without the currency symbol
 */
function getNumberFromFormattedPrice(formattedPrice) {
  const returnItemPriceNumberString = formattedPrice
    .match(/[,.\d]+/)[0]
    .replace(/,/, '.');
  return parseFloat(returnItemPriceNumberString);
}

module.exports = createPrice;
